import { Fragment } from 'react';
import styles from './Tabs.module.css';

export interface TabItem {
  id: string;
  label: string;
  /** Vertical rails only: items sharing a group sit under one heading. */
  group?: string;
}

/**
 * Tab strip. Controlled: the parent owns the active id and renders the matching
 * panel. Horizontal (default) shows an underline indicator; vertical shows a
 * left-hand nav rail with a right-border indicator (collapses back to
 * horizontal on narrow screens). A vertical rail whose items carry a `group`
 * prints the group's heading above its first item.
 */
export function Tabs({
  items,
  active,
  onChange,
  ariaLabel,
  orientation = 'horizontal',
}: {
  items: TabItem[];
  active: string;
  onChange: (id: string) => void;
  ariaLabel?: string;
  orientation?: 'horizontal' | 'vertical';
}) {
  const vertical = orientation === 'vertical';
  return (
    <div
      className={vertical ? `${styles.tablist} ${styles.tablistVertical}` : styles.tablist}
      role="tablist"
      aria-label={ariaLabel}
      aria-orientation={vertical ? 'vertical' : 'horizontal'}
    >
      {items.map((t, i) => (
        <Fragment key={t.id}>
          {vertical && t.group && t.group !== items[i - 1]?.group && (
            <span className={styles.groupLabel} role="presentation">
              {t.group}
            </span>
          )}
          <button
            type="button"
            role="tab"
            aria-selected={active === t.id}
            className={active === t.id ? `${styles.tab} ${styles.tabActive}` : styles.tab}
            onClick={() => onChange(t.id)}
          >
            {t.label}
          </button>
        </Fragment>
      ))}
    </div>
  );
}
